import {SxProps} from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, {ChangeEvent, FC, useState} from 'react';
import {useTranslation} from 'react-i18next';
import 'dayjs/locale/es';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import CachedIcon from '@mui/icons-material/Cached';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

type ProviderCredentials = {
  airbusApiKey: string,
  cleosUsername: string,
  cleosPassword: string
}

export type Props = {
  isLoading: boolean,
  providerCredentials?: Partial<ProviderCredentials>,
  onAirbusSave: (airbusApiKey: string) => void,
  onCleosSave: (credentials: {cleosUsername: string, cleosPassword: string}) => void,
  onProvidersCredentialsChange: (field: keyof ProviderCredentials, value: string) => void,
};

const UpdateProviderCredentialsPanel: FC<Props> = ({
  isLoading,
  providerCredentials,
  onAirbusSave,
  onCleosSave,
  onProvidersCredentialsChange,
}) => {

  const {t} = useTranslation();

  const [showPassword, setShowPassword] = useState(false);

  const handleFieldChange = (field: keyof ProviderCredentials) => (e: ChangeEvent<HTMLInputElement>)=>
    onProvidersCredentialsChange(field, e.target.value);

  const handleAirbusApiKeySave = ()=> {
    const value = providerCredentials?.airbusApiKey;
    if (!value) return;
    onAirbusSave(value);
  };

  const handleCleosSettingsSave = () => {
    const username = providerCredentials?.cleosUsername;
    const password = providerCredentials?.cleosPassword;
    if(!username || !password) return;
    onCleosSave({cleosUsername: username, cleosPassword: password});
  };

  return  <Stack direction="column" sx={styles.container}>
    <Stack direction="column" sx={styles.header}>
      <Typography variant="h5" sx={styles.headerTitle}>{t('providerCredentials.title')}</Typography>
      <Typography sx={styles.headerDescription}>{t('providerCredentials.description')}</Typography>
      <Typography variant="body2" sx={styles.headerNote}>{t('providerCredentials.storedNotShown')}</Typography>
    </Stack>
    <Stack direction="column" sx={styles.formContainer}>
      <Typography variant="overline" sx={{color: 'primary', fontWeight: 700}}>{t('providerCredentials.airbus')}</Typography>
      <Stack direction="column" sx={styles.fieldTitle}>
        <Typography>{t('providerCredentials.apiKey')}</Typography>
        <Stack direction="row" sx={styles.fieldContainer}>
          <TextField value={providerCredentials?.airbusApiKey ?? ''} label="" variant="outlined" size="small" sx={styles.field} onChange={handleFieldChange('airbusApiKey')} autoComplete="off"/>
          <IconButton onClick={handleAirbusApiKeySave} disabled={!providerCredentials || !providerCredentials.airbusApiKey || isLoading} sx={{color: 'primary.main'}}>
            <CachedIcon/>
          </IconButton>
        </Stack>
      </Stack>
    </Stack>

    <Stack direction="column" sx={styles.formContainer}>
      <Typography variant="overline" sx={{color: 'primary', fontWeight: 700}}>{t('providerCredentials.cleos')}</Typography>
      <Stack direction="row" sx={styles.fieldTitle}>
        <Stack direction="column">
          <Typography sx={{mt: 1}}>{t('providerCredentials.username')}</Typography>
          <TextField value={providerCredentials?.cleosUsername ?? ''} label="" variant="outlined" size="small" sx={styles.field} onChange={handleFieldChange('cleosUsername')} autoComplete="off"/>
        </Stack>
        <Stack direction="column">
          <Typography sx={{mt: 1}}>{t('providerCredentials.password')}</Typography>
          <TextField
            value={providerCredentials?.cleosPassword ?? ''}
            label=""
            variant="outlined"
            size="small"
            sx={styles.field}
            onChange={handleFieldChange('cleosPassword')}
            // the panel never shows stored credentials, so browsers would autofill the ImageSearch login here;
            // Chrome ignores autoComplete="off" on password fields, but honours "new-password"
            autoComplete="new-password"
            type={showPassword ? 'text' : 'password'}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}}
          />
        </Stack>
        <IconButton onClick={handleCleosSettingsSave} disabled={!providerCredentials || !providerCredentials.cleosUsername || !providerCredentials.cleosPassword || isLoading} sx={{color: 'primary.main', alignSelf: 'flex-end'}}>
          <CachedIcon/>
        </IconButton>
      </Stack>
    </Stack>
  </Stack>;
};
export default UpdateProviderCredentialsPanel;

const styles: Record<string, SxProps> = {
  container: {
    gap: 1,
    p:1,
    bgcolor: 'grey.200',
  },
  header: {
    px: 2,
    alignItems: 'center'
  },
  headerTitle: {
    fontWeight: 'bold',
    color: 'primary.main'
  },
  headerDescription: {
    color: 'text.primary'
  },
  headerNote: {
    color: 'text.secondary',
    textAlign: 'center'
  },
  formContainer: {
    bgcolor: 'common.white',
    px: 2,
    borderRadius: 2,
    py: 2,
  },
  fieldTitle: {
    gap: 1,
    justifyContent: 'center',
  },
  fieldContainer: {
    width: '100%',
    gap: 1,
  },
  field: {
    flex: 1
  },
};
